import { ContextType } from '@lit/context';
import { SlRadioGroup } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { docLinkStyles } from '../components/cssstyles.js';
import { GithubInstallation } from '../components/pick-github-installation.js';
import { GithubPat } from '../components/pick-github-pat.js';
import { GitlabPat } from '../components/pick-gitlab-pat.js';
import { JsonRepoUpload } from '../components/pick-json-repo.js';
import { LocalCheckout } from '../components/pick-local-checkout.js';
import { consume, contexts, TaskEvent } from '../contexts.js';

/**
 * A repository a project can be linked to, together with whatever credentials
 * that link needs. A Flakiness project is a bucket of reports, so this can be
 * chosen at creation time and swapped later.
 */
export type SourceCodeIntegration =
  | { kind: 'github_app', owner: string, repo: string }
  | { kind: 'github_pat', owner: string, repo: string, accessToken: string }
  | { kind: 'gitlab_pat', owner: string, repo: string, accessToken: string }
  | { kind: 'local_checkout', gitFolder: string, defaultBranch: string }
  | { kind: 'json_repo', jsonRepoPath: string };

export type SourceCodeIntegrationKind = SourceCodeIntegration['kind'];

/**
 * The kinds an existing project can be re-pointed at. Local checkouts and JSON
 * repos are creation-time fixtures, so they are not offered when relinking.
 */
export const RELINKABLE_KINDS: SourceCodeIntegrationKind[] = ['github_app', 'gitlab_pat', 'github_pat'];

/**
 * Fires whenever the picked integration changes. A `detail` of `undefined`
 * means whatever was picked before is no longer valid - the user switched
 * providers, or edited a token the previous pick was made with.
 */
export class SourceCodeIntegrationEvent extends CustomEvent<SourceCodeIntegration|undefined> {
  static eventName = 'source-code-integration';

  constructor(integration: SourceCodeIntegration|undefined) {
    super(SourceCodeIntegrationEvent.eventName, { detail: integration });
  }
}

const SOURCE_LABELS: Record<SourceCodeIntegrationKind, string> = {
  github_app: 'GitHub',
  gitlab_pat: 'GitLab',
  github_pat: 'GitHub Token',
  local_checkout: 'Local Checkout',
  json_repo: 'JSON Repo',
};

const SOURCE_ICONS: Record<SourceCodeIntegrationKind, string|undefined> = {
  github_app: 'github',
  gitlab_pat: 'gitlab',
  github_pat: 'github',
  local_checkout: undefined,
  json_repo: undefined,
};

const SOURCE_BADGES: Record<SourceCodeIntegrationKind, string|undefined> = {
  github_app: undefined,
  gitlab_pat: 'Beta',
  github_pat: undefined,
  local_checkout: undefined,
  json_repo: undefined,
};

/**
 * The repository this project is linked to, if the integration names one.
 * Local checkouts and JSON repos don't.
 */
export function integrationRepo(integration: SourceCodeIntegration|undefined): string|undefined {
  if (!integration)
    return undefined;
  return 'repo' in integration ? integration.repo : undefined;
}

@customElement('feature-source-code-integration')
export class FeatureSourceCodeIntegration extends LitElement {
  @consume({ context: contexts.user }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.serverInfo, subscribe: true }) private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  /**
   * Narrows what may be picked. Relinking an existing project supports fewer
   * kinds than creating one, since a local checkout or JSON repo carries no
   * credentials to swap in.
   */
  @property({ attribute: false }) allowedKinds?: SourceCodeIntegrationKind[];

  @state() private _kind: SourceCodeIntegrationKind = 'github_app';
  @state() private _integration?: SourceCodeIntegration;

  // Easter egg (super admins only): 4 consecutive Shift presses (any other key
  // resets the counter) unlocks the sources that aren't offered by default.
  @state() private _devMode = false;
  private _shiftStreak = 0;
  private _onWindowKeyDown = (e: KeyboardEvent) => {
    if (!this._user?.isSuperUser)
      return;
    if (e.key === 'Shift') {
      if (e.repeat)
        return;
      this._shiftStreak++;
      if (this._shiftStreak >= 4)
        this._devMode = true;
    } else {
      this._shiftStreak = 0;
    }
  };

  override connectedCallback() {
    super.connectedCallback();
    window.addEventListener('keydown', this._onWindowKeyDown);
  }

  override disconnectedCallback() {
    window.removeEventListener('keydown', this._onWindowKeyDown);
    super.disconnectedCallback();
  }

  /** The currently picked integration, if it is complete. */
  value(): SourceCodeIntegration|undefined {
    return this._integration;
  }

  /**
   * The sources on offer, in display order. Everything past the first two is
   * only reachable once the developer-mode easter egg is unlocked.
   */
  private _availableSources(): SourceCodeIntegrationKind[] {
    const sources: SourceCodeIntegrationKind[] = [];
    if (this._serverInfo?.enabledGithub)
      sources.push('github_app');
    sources.push('gitlab_pat');
    if (this._devMode) {
      if (this._serverInfo?.enabledGithub)
        sources.push('github_pat');
      if (this._serverInfo?.enabledFSGitManager)
        sources.push('local_checkout');
      if (this._user?.isSuperUser && this._serverInfo?.isDevMode)
        sources.push('json_repo');
    }
    const allowedKinds = this.allowedKinds;
    return allowedKinds ? sources.filter(source => allowedKinds.includes(source)) : sources;
  }

  override render() {
    const sources = this._availableSources();
    const selected = sources.includes(this._kind) ? this._kind : sources[0];

    return html`
      ${sources.length > 1 ? html`
        <sl-radio-group value=${selected} @sl-change=${(event: Event) => this._onKindChanged((event.target as SlRadioGroup).value as SourceCodeIntegrationKind)}>
          ${sources.map(source => html`
            <sl-radio-button value=${source}>
              ${SOURCE_ICONS[source] ? html`<sl-icon slot=prefix name=${SOURCE_ICONS[source]}></sl-icon>` : nothing}
              ${SOURCE_LABELS[source]}
              ${SOURCE_BADGES[source] ? html`<sl-badge variant=neutral pill>${SOURCE_BADGES[source]}</sl-badge>` : nothing}
            </sl-radio-button>
          `)}
        </sl-radio-group>
      ` : nothing}
      ${this._renderSource(selected)}
    `;
  }

  private _renderSource(source: SourceCodeIntegrationKind|undefined) {
    if (source === 'github_app')
      return html`<pick-github-installation @github-installation=${this._onGithubInstallation}></pick-github-installation>`;
    if (source === 'gitlab_pat')
      return html`<pick-gitlab-pat @gitlab-pat=${this._onGitlabPat}></pick-gitlab-pat>`;
    if (source === 'github_pat') {
      return html`
        <sl-alert variant="warning" open>
          <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
          <strong>Limited integration</strong><br>
          Personal Access Tokens have significant limitations compared to the Github App:
          <ul>
            <li><strong>No push/PR notifications</strong> — new commits and pull requests are detected on a best-effort basis with delays</li>
            <li><strong>No Github Checks</strong> — test results cannot be published as check runs on your pull requests</li>
          </ul>
          For the best experience, use the Github App integration.
        </sl-alert>
        <pick-github-pat @github-pat=${this._onGithubPat}></pick-github-pat>
      `;
    }
    if (source === 'local_checkout')
      return html`<pick-local-checkout @local-checkout=${this._onLocalCheckout}></pick-local-checkout>`;
    if (source === 'json_repo')
      return html`<pick-json-repo @json-repo=${this._onJsonRepo}></pick-json-repo>`;
    return nothing;
  }

  private _onKindChanged(kind: SourceCodeIntegrationKind) {
    this._kind = kind;
    // Whatever was picked belongs to the source we just navigated away from.
    this._emit(undefined);
  }

  private _emit(integration: SourceCodeIntegration|undefined) {
    this._integration = integration;
    this.dispatchEvent(new SourceCodeIntegrationEvent(integration));
  }

  private _onGithubInstallation(event: CustomEvent<GithubInstallation>) {
    this._emit(event.detail ? {
      kind: 'github_app',
      owner: event.detail.owner,
      repo: event.detail.repo,
    } : undefined);
  }

  private _onGithubPat(event: TaskEvent<GithubPat>) {
    this._emit(event.data ? {
      kind: 'github_pat',
      owner: event.data.owner,
      repo: event.data.repo,
      accessToken: event.data.accessToken,
    } : undefined);
  }

  private _onGitlabPat(event: CustomEvent<GitlabPat|undefined>) {
    this._emit(event.detail ? {
      kind: 'gitlab_pat',
      owner: event.detail.owner,
      repo: event.detail.repo,
      accessToken: event.detail.accessToken,
    } : undefined);
  }

  private _onLocalCheckout(event: TaskEvent<LocalCheckout>) {
    this._emit(event.data ? {
      kind: 'local_checkout',
      gitFolder: event.data.gitFolder,
      defaultBranch: event.data.defaultBranch,
    } : undefined);
  }

  private _onJsonRepo(event: TaskEvent<JsonRepoUpload>) {
    this._emit(event.data ? {
      kind: 'json_repo',
      jsonRepoPath: event.data.jsonRepoPath,
    } : undefined);
  }

  static styles = [docLinkStyles, css`
    :host {
      display: block;
    }

    /* Separates the source radio group from whichever picker it reveals. */
    sl-radio-group {
      margin-bottom: var(--sl-spacing-medium);
    }

    sl-badge {
      margin-left: var(--sl-spacing-2x-small);
      vertical-align: middle;
    }

    /* Shoelace floors badges at 12px, which is loud next to a radio label. */
    sl-badge::part(base) {
      font-size: var(--sl-font-size-2x-small);
      padding: 0.25em 0.5em;
    }

    pick-github-installation {
      margin-bottom: var(--sl-spacing-medium);
    }

    sl-alert {
      margin-top: var(--sl-spacing-medium);
    }
  `];
}
